import { IGenre } from 'services/enums/types';
import { ISettlementInfoContractForm } from 'services/music-product-digital/types/form';
import { ISettlementInfoContractMutation } from 'services/music-product-digital/types/mutation';
import {
  IArtistWithRoleForm,
  IArtistWithRoleMutation,
  ICopyrights,
  IExternalId,
  IInfinitePageParams,
  IInfinitePageResponse,
  ITitle,
  IVersion,
  IWaitingForFile,
  STATUS,
} from 'services/types';

export interface IVideoFilePath {
  videoLink?: string | null;
  originalVideoLink?: string | null;
}

export interface IVideoSubtitles {
  subtitles: string;
  language: number;
}

export interface IVideoArtistMutation extends IArtistWithRoleMutation {}

export interface IVideoArtistMutationForm extends IArtistWithRoleForm {
  isDuplicated: boolean;
}

export interface IVideoBase {
  pline: ICopyrights | null;
  cline: ICopyrights | null;
  language: number;

  isCreatedWithAi: boolean;
  // source: TRACK_SOURCE;
  otherTitles: ITitle[];
  subtitles: IVideoSubtitles[];
  externalIds: IExternalId[];
  mainTitle: ITitle;
}

export interface IVideoMutation extends IVideoBase {
  status: STATUS;
  artists: IVideoArtistMutation[];
  isInstrumental: boolean;
  isrc: string | null;
  genre: number | null;
  censorious: TRACK_CENSORIOUS | null;
  contractRequests: ISettlementInfoContractMutation[];
}

export interface IVideoMutationForm
  extends Omit<
      IVideoMutation,
      'artists' | 'genre' | 'censorious' | 'contractRequests'
    >,
    IVideoFilePath, IVersion {
  file?: File;
  artists: IVideoArtistMutationForm[];
  isrc: string;
  canUpdateISRC: boolean | null;
  genre: IGenre;
  censorious: TRACK_CENSORIOUS;
  hasDuplicatedArtist: boolean | null;
  contracts: ISettlementInfoContractForm[];
}

export interface IVideoId {
  videoId: string;
}

export interface ICreateVideo extends IVideoMutation, IWaitingForFile {}

export interface IUpdateVideo
  extends IVideoMutation,
    IWaitingForFile,
    IVideoId, IVersion {}

export interface IVideoArtistResponse extends IVideoArtistMutationForm {
  rolePlacement: number;
  artistStatus?: STATUS;
  pictureId?: string;
  picturePath?: string;
  pictureExtension?: string;
  previewPicture?: string;
}

export interface IVideoResponse extends IVideoBase, IVideoFilePath, IVersion {
  id: string;
  videoStatus: STATUS;
  artists: IVideoArtistResponse[];
  canUpdateISRC: boolean;
  genre: IGenre | null;
  censorious: TRACK_CENSORIOUS | null;
  isrc: string | null;
  instrumental?: boolean;
  hasDuplicatedArtist: boolean | null;
  source?: TRACK_SOURCE | null;
  canRemoveContracts?: boolean;
  contracts: ISettlementInfoContractForm[];
}

export interface IVideoPlaybackTicket {
  ticket: string;
  expiresAtUtc: string;
  playbackUrl: string;
}

export interface IVideosResponse extends IVideoResponse {}

export interface IVideoPageResponse
  extends IInfinitePageResponse<IVideosResponse> {}

export interface IVideoSearch extends IInfinitePageParams {
  title?: string;
  isrc?: string;
  ean?: string;
  artistNickname?: string;
  artistName?: string;
  hasDuplicatedArtist?: boolean;
  hasContracts?: boolean;
}

export interface IVideoLight {
  id: string;
  mainTitle: string;
}

export interface IVideoFileUploadComplete extends IVersion {
  file: File;
  videoId: string;
}

export enum TRACK_CENSORIOUS {
  unknown = -1,
  explicit = 1,
  notExplicit = 2,
  clean = 3,
}

export enum TRACK_SOURCE {
  unknown = -1,
  local = 1,
  fromDistribution = 2,
}

export interface IVideoContract {
  id: string;
  contractId: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  labelId: string;
  labelName: string;
  divisionPercentage: number;
  contractName: string;
}

export interface IVideoContractsTime {
  id: string;
  contracts: IVideoContract[];
}
